import React, { useState } from 'react';
import Icon from "../../components/Icon";

const ReviewCard = React.memo(({ review, reviewerName, reviewerAvatar, collaborationMonth, propertyName, rating, reviewTitle, reviewText, response, responseLabel, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  console.log(`ReviewCard ${index} rendered. isExpanded:`, isExpanded);
  
  const shouldTruncate = reviewText && reviewText.length > 150;
  
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
  };
  
  const displayText = isExpanded || !shouldTruncate ? reviewText : truncateText(reviewText);
  
  const toggleExpansion = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (shouldTruncate) {
      console.log('Toggling review card, current isExpanded:', isExpanded, 'index:', index);
      setIsExpanded(prev => !prev);
    }
  };
  
  return (
    <div 
      onClick={toggleExpansion}
      style={{
        padding: '24px',
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e8e8e8',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'box-shadow 0.2s ease',
        cursor: shouldTruncate ? 'pointer' : 'default',
        position: 'relative',
        minHeight: '320px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header: Reviewer info and rating */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '12px',
        gap: '16px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '6px'
          }}>
            {reviewerAvatar && (
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                overflow: 'hidden',
                background: '#f0f0f0',
                flexShrink: 0,
                border: '2px solid #ffffff',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <img 
                  src={reviewerAvatar} 
                  alt="Reviewer" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover' 
                  }} 
                />
              </div>
            )}
            <div>
              <div style={{
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                color: '#0f084b',
                lineHeight: '1.3'
              }}>
                {reviewerName || 'Reviewer'}
              </div>
              {collaborationMonth && (
                <div style={{
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '13px',
                  color: '#888',
                  marginTop: '2px'
                }}>
                  {collaborationMonth}
                  {propertyName && ` • ${propertyName}`}
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0
        }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span key={star} style={{
              color: star <= rating ? '#dc1d08' : '#e0e0e0',
              fontSize: '18px',
              lineHeight: '1'
            }}>
              ★
            </span>
          ))}
        </div>
      </div>

      {/* Review Title */}
      {reviewTitle && (
        <h4 style={{
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: '16px',
          fontWeight: 600,
          color: '#0f084b',
          margin: '0 0 10px 0',
          lineHeight: '1.4'
        }}>
          {reviewTitle}
        </h4>
      )}

      {/* Review Text Container */}
      <div style={{ position: 'relative' }}>
        <p style={{
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: '15px',
          color: '#333',
          lineHeight: '1.6',
          margin: '0 0 0 0',
          whiteSpace: 'pre-wrap'
        }}>
          {displayText}
        </p>
        
        {/* Gradient fade overlay when collapsed */}
        {!isExpanded && shouldTruncate && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60px',
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
            pointerEvents: 'none'
          }} />
        )}
      </div>

      {/* Expand/Collapse Indicator */}
      {shouldTruncate && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: '#0f084b',
            fontSize: '14px',
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontWeight: 500
          }}>
            <span>{isExpanded ? 'Show less' : 'Show more'}</span>
            <div style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Icon name="arrow-bottom" size="12" />
            </div>
          </div>
        </div>
      )}

      {/* Response - only show when expanded */}
      {isExpanded && response && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          background: '#f8f9fa',
          borderRadius: '12px',
          borderLeft: '4px solid #dc1d08'
        }}>
          <div style={{
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontSize: '13px',
            fontWeight: 600,
            color: '#0f084b',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {responseLabel}
          </div>
          <p style={{
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontSize: '14px',
            color: '#333',
            lineHeight: '1.5',
            margin: 0
          }}>
            {response}
          </p>
        </div>
      )}
    </div>
  );
});

ReviewCard.displayName = 'ReviewCard';

export default ReviewCard;

